import {Injectable} from '@angular/core';
import {Capacitor} from '@capacitor/core';
import {
  Geolocation,
  GeolocationPermissionType,
  PermissionStatus as GeolocationPermissionStatus
} from '@capacitor/geolocation';
import {Filesystem, PermissionStatus as FilesystemPermissionStatus} from '@capacitor/filesystem';
import {Camera, PermissionStatus as CameraPermissionStatus} from '@capacitor/camera';
import BackgroundGeolocation from '@transistorsoft/capacitor-background-geolocation';

@Injectable({
  providedIn: 'root'
})
export class PermissionsService {
  public readonly PERMISSION = {
    ACCESS_COARSE_LOCATION: 'ACCESS_COARSE_LOCATION',
    ACCESS_BACKGROUND_LOCATION: 'ACCESS_BACKGROUND_LOCATION',
    CAMERA: 'CAMERA',
    READ_MEDIA_IMAGES: 'READ_MEDIA_IMAGES',
    WRITE_EXTERNAL_STORAGE: 'WRITE_EXTERNAL_STORAGE'
  };

  async hasLocationPermission(): Promise<boolean> {
    const status = await this.getLocationPermissionStatus();
    if (!status) {
      return false;
    }
    return status.location === 'granted' || status.coarseLocation === 'granted';
  }

  async requestLocationPermission(): Promise<boolean> {
    const permissions: GeolocationPermissionType[] = Capacitor.getPlatform() === 'ios'
      ? ['location']
      : ['coarseLocation'];
    let status: GeolocationPermissionStatus;
    try {
      status = await Geolocation.requestPermissions({permissions});
    } catch (e) {
      if (!this.isLocationServicesDisabledError(e)) {
        console.warn('Failed to request location permission', e);
      }
      return false;
    }
    return status.location === 'granted' || status.coarseLocation === 'granted';
  }

  async isLocationServicesEnabled(): Promise<boolean> {
    try {
      const providerState = await BackgroundGeolocation.getProviderState();
      return providerState?.enabled !== false;
    } catch (e) {
      console.warn('Failed to check location services state', e);
      return true;
    }
  }

  async hasBackgroundLocationPermission(): Promise<boolean> {
    try {
      const providerState = await BackgroundGeolocation.getProviderState();
      return providerState.status !== BackgroundGeolocation.AUTHORIZATION_STATUS_DENIED;
    } catch (e) {
      console.warn('Failed to check background location permission', e);
      return false;
    }
  }

  async requestBackgroundLocationPermission(): Promise<boolean> {
    try {
      const status = await BackgroundGeolocation.requestPermission();
      return status === BackgroundGeolocation.AUTHORIZATION_STATUS_ALWAYS ||
        status === BackgroundGeolocation.AUTHORIZATION_STATUS_WHEN_IN_USE;
    } catch (e) {
      console.warn('Failed to request background location permission', e);
      return false;
    }
  }

  async requestFilesystemPermission(): Promise<boolean> {
    const status: FilesystemPermissionStatus = await Filesystem.checkPermissions();
    if (status.publicStorage === 'granted') {
      return true;
    }
    const requested = await Filesystem.requestPermissions();
    return requested.publicStorage === 'granted';
  }

  async hasFilesystemPermission(): Promise<boolean> {
    const status: FilesystemPermissionStatus = await Filesystem.checkPermissions();
    return status.publicStorage === 'granted';
  }

  async hasCameraPermission(): Promise<CameraPermissionStatus> {
    try {
      return await Camera.checkPermissions();
    } catch (e) {
      console.warn('Failed to check camera permission', e);
      return {camera: 'prompt'} as any;
    }
  }

  private async getLocationPermissionStatus(): Promise<GeolocationPermissionStatus | null> {
    try {
      return await Geolocation.checkPermissions();
    } catch (e) {
      if (!this.isLocationServicesDisabledError(e)) {
        console.warn('Failed to check location permission', e);
      }
      return null;
    }
  }

  private isLocationServicesDisabledError(error: any): boolean {
    return error?.code === 'OS-PLUG-GLOC-0007';
  }
}
